import {
  OrganizationApiData,
  OrganizationApiResponse,
  OrganizationEnabledFeatures,
} from '../types/api.types';
import { DataContext } from '../types/data.types';
import { Data } from './Data';

export interface OrganizationStatus {
  label: string;
}

export interface OrganizationStatuses {
  trial: OrganizationStatus;
  active: OrganizationStatus;
  inactive: OrganizationStatus;
  archived: OrganizationStatus;
  resetting: OrganizationStatus;
}

// Re-export for backwards compatibility
export type { OrganizationApiData, OrganizationApiResponse };

export interface OrganizationOptions {
  resolve: (org: Organization, cached: boolean) => void;
  reject: (error: unknown) => void;
  context: DataContext;
}

export class Organization extends Data {
  static instance: Organization | null = null;

  //OrganizationStatus { Trial, Active, Inactive, Archived }
  static status: OrganizationStatuses = {
    trial: {
      label: 'Trial',
    },
    active: {
      label: 'Active',
    },
    inactive: {
      label: 'Inactive',
    },
    archived: {
      label: 'Archived',
    },
    resetting: {
      label: 'Resetting',
    },
  };

  organizationId!: string;
  name!: string;
  showProficiency!: boolean;
  trialEndDate!: Date | null;
  isDemo!: boolean;
  status!: OrganizationStatus | undefined;
  demoOrgStatus!: OrganizationStatus | undefined;
  isTrial!: boolean;
  hasIntegration!: boolean;
  hasActiveIntegration!: boolean;
  hasMultipleIntegrations!: boolean;
  address!: string;
  hasRegulationTasks!: boolean;
  trialMillisecondsRemaining!: number;
  trialDaysRemaining!: number;
  trialExpired!: boolean;
  hasRegulations!: boolean;
  enableJobMatching!: boolean;
  allowJobMatchingConfig!: boolean;
  enableStandardsLibrary!: boolean;
  defaultLanguageCode!: string;
  employeeLandingPage!: number;
  /** Practicals (October #4): show attachments and checklists to participants by default. */
  includePracticalAttachmentsForParticipants!: boolean;
  /** Practicals: an instructor may override the default above per practical. */
  allowInstructorOverridePracticalAttachments!: boolean;
  isPaidPlan!: boolean;

  // Matches KlayoLib.Model.EmployeeLandingPage (serialized as int)
  static employeeLandingPage = {
    Profile: 0,
    MyKlayo: 1,
  } as const;

  static coreFeatures = ['Competencies', 'Employees', 'Jobs', 'Regulations', 'Tasks'] as const;

  enabledFeatures?: OrganizationEnabledFeatures;
  isEnabledCoreFeatures!: boolean;

  constructor(options: OrganizationOptions) {
    super({
      api: '/Organization',
      callback: () => {
        options.resolve(this, false);
      },
      ...options,
    });
  }

  onDataReceived(data: OrganizationApiResponse): void {
    this.organizationId = data.organization.organizationId;
    this.name = data.organization.name;
    this.showProficiency = data.organization.showProficiency;
    this.trialEndDate = data.organization.trialEndDate
      ? new Date(data.organization.trialEndDate)
      : null;
    this.isDemo = data.organization.isDemo;
    const statusValues = Object.values(Organization.status);
    // Validate status index before assignment to prevent undefined access
    const statusIndex = data.organization.status;
    this.status =
      statusIndex >= 0 && statusIndex < statusValues.length
        ? (statusValues[statusIndex] as OrganizationStatus)
        : undefined;

    const demoStatusIndex = data.organization.demoOrgStatus;
    this.demoOrgStatus =
      demoStatusIndex !== undefined && demoStatusIndex >= 0 && demoStatusIndex < statusValues.length
        ? (statusValues[demoStatusIndex] as OrganizationStatus)
        : undefined;
    this.isTrial = this.status === Organization.status.trial;
    this.hasIntegration = data.organization.hasIntegration;
    this.hasActiveIntegration = data.organization.hasActiveIntegration;
    this.hasMultipleIntegrations = data.organization.hasMultipleIntegrations;
    this.address = data.organization.address || '';
    this.hasRegulationTasks = data.organization.hasRegulationTasks;
    this.enableJobMatching = data.organization.enableJobMatching;
    this.hasRegulations = data.organization.hasRegulations;
    this.allowJobMatchingConfig = data.organization.allowJobMatchingConfig;
    this.enableStandardsLibrary = data.organization.enableStandardsLibrary;
    this.defaultLanguageCode = data.organization.defaultLanguageCode || 'en';
    this.employeeLandingPage =
      data.organization.employeeLandingPage ?? Organization.employeeLandingPage.Profile;
    this.includePracticalAttachmentsForParticipants =
      data.organization.includePracticalAttachmentsForParticipants ?? true;
    this.allowInstructorOverridePracticalAttachments =
      !!data.organization.allowInstructorOverridePracticalAttachments;
    this.isPaidPlan = data.organization.isPaidPlan;
    this.enabledFeatures = data.organization.enabledFeatures;
    this.isEnabledCoreFeatures = Organization.coreFeatures.every(
      (k) => !!this.enabledFeatures?.[k]
    );

    if (this.isTrial && this.trialEndDate) {
      const today = new Date();
      this.trialMillisecondsRemaining = this.trialEndDate.getTime() - today.getTime();
      this.trialDaysRemaining = Math.floor(this.trialMillisecondsRemaining / (1000 * 60 * 60 * 24));
      this.trialExpired = this.trialMillisecondsRemaining < 0;
    }
  }

  static getOrganization(context: DataContext): Promise<{ data: Organization; cached: boolean }> {
    return new Promise((resolve, reject) => {
      if (Organization.instance) {
        resolve({ data: Organization.instance, cached: true });
      } else {
        Organization.instance = new Organization({
          resolve: (org: Organization) => resolve({ data: org, cached: false }),
          reject,
          context,
        });
      }
    });
  }
}
